import { Observable } from 'rxjs';
import {
  DealPartner,
  DmpAudienceProvider,
  JapiQuery,
  PartnerType,
  Portfolio,
  ResponseFromServer,
  RoleJava,
  UserRoleType
} from '../../shared/_models/models';
import { FusionApiHttpClient } from '../../shared/_services/server-clients';

export class ExchangePartnersApiService {
  constructor(private readonly fusionApiHttpClient: FusionApiHttpClient) {}

  getDealPartners(query: JapiQuery = {}): Observable<ResponseFromServer<DealPartner[]>> {
    return this.fusionApiHttpClient.post<DealPartner[], JapiQuery>('deal-partners/get', query);
  }

  getAgencyPartners(): Observable<ResponseFromServer<DealPartner[]>> {
    return this.getDealPartners(this.buildEqualsQuery('partnerType', PartnerType.AGENCY));
  }

  /** Returns the partner plus every agency below it, so nested children are included. */
  getDescendantPartners(partnerId: number): Observable<ResponseFromServer<DealPartner[]>> {
    return this.getDealPartners(this.buildEqualsQuery('agencyHierarchyOf', partnerId));
  }

  getActiveDealPartners(): Observable<ResponseFromServer<DealPartner[]>> {
    return this.getDealPartners({
      filter: { filters: [{ fieldName: 'status', operation: 'IN', value: ['ACTIVE'] }] }
    });
  }

  getDealPartner(id: number): Observable<DealPartner> {
    return this.fusionApiHttpClient.plain.get<DealPartner>(`deal-partners/${id}`);
  }

  createDealPartner(payload: DealPartner): Observable<DealPartner> {
    return this.fusionApiHttpClient.plain.post<DealPartner, DealPartner>('deal-partners', payload);
  }

  updateDealPartner(id: number, payload: DealPartner): Observable<DealPartner> {
    return this.fusionApiHttpClient.plain.put<DealPartner, DealPartner>(`deal-partners/${id}`, payload);
  }

  deleteDealPartner(id: number): Observable<DealPartner> {
    return this.fusionApiHttpClient.plain.delete<DealPartner>(`deal-partners/${id}`);
  }

  getDmpAudienceProviders(): Observable<DmpAudienceProvider[]> {
    return this.fusionApiHttpClient.plain.get<DmpAudienceProvider[]>('dmp-audience-providers/');
  }

  getPortfoliosByHierarchy(partnerId: number): Observable<ResponseFromServer<Portfolio[]>> {
    return this.fusionApiHttpClient.get<Portfolio[]>(`portfolios?agencyHierarchyOf=${partnerId}`);
  }

  getPortfoliosByPartner(partnerId: number): Observable<ResponseFromServer<Portfolio[]>> {
    return this.fusionApiHttpClient.get<Portfolio[]>(`portfolios?partnerId=${partnerId}`);
  }

  createPortfolio(partnerId: number, name: string): Observable<Portfolio> {
    return this.fusionApiHttpClient.plain
      .post<Portfolio, Partial<Portfolio>>(`deal-partners/${partnerId}/portfolios`, { name });
  }

  deletePortfolio(portfolioId: number): Observable<void> {
    return this.fusionApiHttpClient.plain.delete<void>(`portfolios/${portfolioId}`);
  }

  /** The partner roles a partner user can hold: Admin, Trader, Viewer. */
  getPartnerRoles(): Observable<ResponseFromServer<RoleJava[]>> {
    return this.fusionApiHttpClient.post<RoleJava[], JapiQuery>('role/get', {
      filter: {
        filters: [
          { fieldName: 'deletedAt', operation: 'IS_NULL', value: '' },
          { fieldName: 'roleType', operation: 'EQUALS', value: UserRoleType.EXTERNAL }
        ]
      }
    });
  }

  private buildEqualsQuery(fieldName: string, value: string | number): JapiQuery {
    return {
      filter: { filters: [{ fieldName, operation: 'EQUALS', value }] },
      sorting: { sortDirection: 'ASC', sortingField: 'name' }
    };
  }
}
